import type { RawAttribute, SpatialAnchor } from '@manudev/model';
import type { PropsElement } from '../props';
import type { BuildStore } from '../types';
import { outerFootprint } from './footprint';
import { ifcFromSite, nativeLength, type Point2 } from './frame';
import { isRoofElement } from './findings';
import { elevationOf, orderedStoreys, sourceStorey, storeyHeight } from './storeys';
import type { BuildParams, BuildResult } from './types';

const THICKNESS = 0.2;
const PITCH = Math.PI / 6;
const OVERHANG = 0.3;
// A footprint this close to its bounding rectangle is a rectangle, and a rectangle gets a gable.
const RECTANGULAR = 0.95;

// A roof over the top storey's outline, sitting on top of its walls: flat, or a gable with the
// ridge along the outline's longer side. With replace the roof that is already up there goes
// first, aggregate and carrying slabs alike.
export async function addRoof(store: BuildStore, params: BuildParams): Promise<BuildResult> {
  const storeys = await orderedStoreys(store);
  const source = await sourceStorey(store, params.storey);
  const height = params.height ?? storeyHeight(storeys, source);
  const footprint = params.footprint?.length
    ? params.footprint.map(plan)
    : outerFootprint(await store.wallSegments(source.expressID));
  if (footprint.length < 3) throw new Error(`no footprint on ${source.name}`);
  const thickness = params.thickness ?? THICKNESS;
  const gable = params.kind ? params.kind === 'gable' : rectangular(footprint);

  const removed: number[] = [];
  if (params.replace) {
    for (const el of replaceable(await store.elements())) {
      await store.remove(el.expressID);
      removed.push(el.expressID);
    }
  }
  const took = removed.length ? `, ${removed.length} old roof parts removed` : '';

  if (!gable) {
    const roofId = await store.addRoof(source.expressID, {
      Profile: 'polygon',
      OuterCurve: footprint,
      Position: [0, 0, height],
      Thickness: thickness,
      Name: params.name ?? `${source.name} roof`,
    });
    return {
      created: [roofId],
      removed,
      note: `flat roof over ${footprint.length} points at ${elevationOf(source) + height} m${took}`,
    };
  }

  const anchor = await store.anchor(source.expressID);
  const box = bounds(footprint);
  const roofId = await emitGable(store, anchor, {
    box,
    base: height,
    thickness,
    name: params.name ?? `${source.name} roof`,
  });
  const rise = (Math.min(box.width, box.depth) / 2) * Math.tan(PITCH);
  return {
    created: [roofId],
    removed,
    note: `gable roof over ${box.width.toFixed(1)} by ${box.depth.toFixed(1)} m at ${elevationOf(source) + height} m, ${rise.toFixed(2)} m to the ridge${took}`,
  };
}

interface Bounds {
  centre: Point2;
  width: number;
  depth: number;
}

interface GableParams {
  box: Bounds;
  // top of the walls, in storey-local metres
  base: number;
  thickness: number;
  name: string;
}

// ifc-lite's in-store roof is flat only, so the gable is hand-emitted: one closed section in
// the plane across the ridge, swept the length of it. The section is the roof's own shape, so
// the extrusion carries the whole roof rather than a pair of planes that miss at the ridge.
async function emitGable(
  store: BuildStore,
  anchor: SpatialAnchor,
  params: GableParams,
): Promise<number> {
  const { box, thickness } = params;
  const alongX = box.width >= box.depth;
  const ridge: Point2 = alongX ? [1, 0] : [0, 1];
  // the section's own X, so that its Y comes out as world up
  const across: Point2 = [-ridge[1], ridge[0]];
  const half = Math.min(box.width, box.depth) / 2;
  const rise = half * Math.tan(PITCH);
  const length = Math.max(box.width, box.depth) + 2 * OVERHANG;

  const owner = anchor.ownerHistoryId != null ? `#${anchor.ownerHistoryId}` : null;
  const metres = (v: number): RawAttribute => ({ real: nativeLength(anchor, v) });
  const ratio = (v: number): RawAttribute => ({ real: v });
  const raw = (type: string, attributes: RawAttribute[]) =>
    store.addRaw(type, attributes, { product: false });

  const section: Point2[] = [
    [-half, 0],
    [0, rise],
    [half, 0],
    [half, thickness],
    [0, rise + thickness],
    [-half, thickness],
  ];
  const points: number[] = [];
  for (const [u, v] of section)
    points.push(await raw('IfcCartesianPoint', [[metres(u), metres(v)]]));
  points.push(points[0]!);
  const outline = await raw('IfcPolyline', [points.map((id) => `#${id}`)]);
  const profile = await raw('IfcArbitraryClosedProfileDef', ['.AREA.', 'Gable', `#${outline}`]);

  const origin = await raw('IfcCartesianPoint', [[metres(0), metres(0), metres(0)]]);
  const sweep = await raw('IfcDirection', [[ratio(ridge[0]), ratio(ridge[1]), ratio(0)]]);
  const span = await raw('IfcDirection', [[ratio(across[0]), ratio(across[1]), ratio(0)]]);
  const frame = await raw('IfcAxis2Placement3D', [`#${origin}`, `#${sweep}`, `#${span}`]);
  const extrude = await raw('IfcDirection', [[ratio(0), ratio(0), ratio(1)]]);
  const solid = await raw('IfcExtrudedAreaSolid', [
    `#${profile}`,
    `#${frame}`,
    `#${extrude}`,
    metres(length),
  ]);
  const body = await raw('IfcShapeRepresentation', [
    `#${anchor.bodyContextId}`,
    'Body',
    'SweptSolid',
    [`#${solid}`],
  ]);
  const shape = await raw('IfcProductDefinitionShape', [null, null, [`#${body}`]]);

  const at = await raw('IfcCartesianPoint', [
    [
      metres(box.centre[0] - (ridge[0] * length) / 2),
      metres(box.centre[1] - (ridge[1] * length) / 2),
      metres(params.base),
    ],
  ]);
  const local = await raw('IfcAxis2Placement3D', [`#${at}`, null, null]);
  const placement = await raw('IfcLocalPlacement', [`#${anchor.storeyPlacementId}`, `#${local}`]);

  const roofId = await store.addRaw('IfcRoof', [
    await store.guid(),
    owner,
    params.name,
    null,
    null,
    `#${placement}`,
    `#${shape}`,
    null,
    '.GABLE_ROOF.',
  ]);
  await raw('IfcRelContainedInSpatialStructure', [
    await store.guid(),
    owner,
    null,
    null,
    [`#${roofId}`],
    `#${anchor.storeyId}`,
  ]);
  return roofId;
}

// Whatever currently serves as the roof, wherever it sits: the same elements findings.ts
// measures it by. Not gated by elevation, since a roof built for a shorter building sits well
// below where a taller one's top now is.
function replaceable(elements: PropsElement[]): PropsElement[] {
  return elements.filter(isRoofElement);
}

function bounds(footprint: Point2[]): Bounds {
  const xs = footprint.map((p) => p[0]);
  const ys = footprint.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  return {
    centre: [(minX + maxX) / 2, (minY + maxY) / 2],
    width: maxX - minX,
    depth: maxY - minY,
  };
}

function rectangular(footprint: Point2[]): boolean {
  const box = bounds(footprint);
  const rect = box.width * box.depth;
  return rect > 0 && area(footprint) / rect >= RECTANGULAR;
}

function area(ring: Point2[]): number {
  let sum = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++)
    sum += ring[j]![0] * ring[i]![1] - ring[i]![0] * ring[j]![1];
  return Math.abs(sum) / 2;
}

// A footprint the caller gave is a site ground plan: x to the right, z into the screen.
const plan = (p: Point2): Point2 => {
  const [x, y] = ifcFromSite([p[0], 0, p[1]]);
  return [x, y];
};
